import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { userSelectedFinanceTab, userSelectedLeaseTab } from '../../../redux/actions/bestOffers'
import { getLeaseByTerm, getFinanceByTerm } from '../../../redux/selectors/bestOffer';
import { isPaymentOptionsLoading } from '../../../redux/selectors/paymentOptions';
import { isEmpty, geti18n } from '../../../utils';
import BestOfferLoader from './BestOfferLoader';
import ErrorMessage from '../../../components/ErrorMessage';
import BestOfferProvider from './BestOfferProvider';
import './styles';

/**
 * This Class Handles the separation between the Lease and Finance
 * options and provides the separate cards with the information for
 * creating them.
 */
class BestOfferList extends Component {
  static propTypes = {
    bestLease: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.bool
    ]).isRequired,
    bestFinance: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.bool
    ]).isRequired,
    loading: PropTypes.bool.isRequired,
    mediaType: PropTypes.string.isRequired,
    brandName: PropTypes.string.isRequired,
    noIncentivesTitle: PropTypes.string,
    noIncentivesMessage: PropTypes.string,
    noIncentivesLinkText: PropTypes.string,
    noIncentivesLinkURL: PropTypes.string,
    leaseHeading: PropTypes.string,
    financeHeading: PropTypes.string,
    financeSelectDispatch: PropTypes.func.isRequired,
    leaseSelectDispatch: PropTypes.func.isRequired
  };

  static defaultProps = {
    noIncentivesTitle: geti18n('noIncentives.title'),
    noIncentivesMessage: geti18n('noIncentives.message'),
    noIncentivesLinkText: geti18n('noIncentives.linkText'),
    noIncentivesLinkURL: geti18n('noIncentives.linkURL'),
    leaseHeading: geti18n('paymentTypes.lease'),
    financeHeading: geti18n('paymentTypes.finance'),
  };

  constructor(props) {
    super(props);
    this.rootNode = null;
    this.changeTab = this.changeTab.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.renderBestFinanceOption = this.renderBestFinanceOption.bind(this);
    this.renderBestLeaseOption = this.renderBestLeaseOption.bind(this);
    this.renderEmptyOptions = this.renderEmptyOptions.bind(this);
    this.state = {
      activeTab: props.brandName === 'infiniti' ? 'lease' : 'finance'
    }
  }

  componentDidMount() {
    this.handleResize();
    addEventListener('resize', this.handleResize);
  }

  componentDidUpdate() {
    this.handleResize()
  }

  componentWillUnmount() {
    this.rootNode = null;
    removeEventListener('resize', this.handleResize)
  }

  // Handler for the keeping the card heights the same on resizing of window
  handleResize() {
    this.equalizeHeights();
  }

  equalizeNode(selector) {
    const node = this.rootNode;
    if (!node) {
      return;
    }
    /* Get the card nodes as an array */
    const targets = [].slice.call(node.querySelectorAll(selector));
    if (isEmpty(targets)) {
      return;
    }
    /* Get the max height of the cards */
    const maxHeight = targets.reduce((acc, val) => {
      const targetElem = val;
      targetElem.style.height = 'auto';
      const { height: targetHeight } = val.getBoundingClientRect();
      return targetHeight > acc ? targetHeight : acc;
    }, 0);
    /* Set all card heights equal to the target height */
    targets.forEach((top) => {
      const target = top;
      target.style.height = `${maxHeight}px`;
    });
  }

  // Handler to equalize the heights of the two cards
  equalizeHeights() {
    this.equalizeNode('.offer-top');
    this.equalizeNode('.offer-summary-rows');
  }

  // Changes the active tab for mobile view
  changeTab(tab) {
    const { financeSelectDispatch, leaseSelectDispatch } = this.props;
    if (tab === 'lease') {
      leaseSelectDispatch();
    } else if (tab === 'finance') {
      financeSelectDispatch();
    }
    this.setState({
      activeTab: tab
    })
  }

  // If there are no available options for a card display the error
  renderEmptyOptions() {
    const {
      noIncentivesTitle,
      noIncentivesMessage,
      noIncentivesLinkText,
      noIncentivesLinkURL,
      brandName
    } = this.props;

    // Need to set the SVG color to black for Infiniti or white for Nissan
    const errorColor = brandName === 'nissan' ? '#fff' : '#000';
    /* eslint-disable */
    return (
      <div className="columns medium-6 lease-payment-options">
        <ErrorMessage
          title={noIncentivesTitle}
          message={noIncentivesMessage}
          errorColor={errorColor}
          linkText={noIncentivesLinkText}
          linkURL={noIncentivesLinkURL}
        />
      </div>
    );
    /* eslint-enable */
  }

  // Send the best finance option to the card
  renderBestFinanceOption() {
    const { mediaType, bestFinance } = this.props;
    const paymentOption = bestFinance;
    if (mediaType === 'small' && this.state.activeTab !== 'finance') {
      return undefined;
    }
    if (!bestFinance) {
      return this.renderEmptyOptions()
    }
    const idKeyValue = `Finance-Best-${paymentOption.theOfferData.promoId}`;

    return (
      <div className="columns medium-6 purchase-payment-options">
        <BestOfferProvider
          id={idKeyValue}
          key={idKeyValue}
          onChange={this.handleResize}
          paymentOption={paymentOption}
          offerType="FINANCE"
          isBest="true"
        />
      </div>
    )
  }

  // Send the best Lease offer to the card
  renderBestLeaseOption() {
    const { mediaType, bestLease } = this.props;
    const paymentOption = bestLease;
    if (mediaType === 'small' && this.state.activeTab !== 'lease') {
      return undefined;
    }
    if (!bestLease) {
      return this.renderEmptyOptions()
    }
    const idKeyValue = `Lease-Best-${paymentOption.theOfferData.promoId}`;

    return (
      <div className="columns medium-6 lease-payment-options">
        <BestOfferProvider
          id={idKeyValue}
          key={idKeyValue}
          onChange={this.handleResize}
          paymentOption={paymentOption}
          offerType="LEASE"
          isBest="true"
        />
      </div>
    )
  }

  // Render the two options, On Infiniti Lease offer displays first
  // On Nissan Display Finance first
  renderPaymentOptions() {
    const {
      loading,
      mediaType,
      brandName,
      leaseHeading,
      financeHeading,
      bestLease,
      bestFinance
    } = this.props;
    const { activeTab } = this.state;
    const { EmptyOptions } = bestLease || bestFinance;
    const heading1 = brandName === 'infiniti' ? leaseHeading : financeHeading;
    const heading2 = brandName === 'infiniti' ? financeHeading : leaseHeading;
    const tab1 = brandName === 'infiniti' ? 'lease' : 'finance';
    const tab2 = brandName === 'infiniti' ? 'finance' : 'lease';
    const option1 = brandName === 'infiniti' ? this.renderBestLeaseOption() : this.renderBestFinanceOption();
    const option2 = brandName === 'infiniti' ? this.renderBestFinanceOption() : this.renderBestLeaseOption();

    if (EmptyOptions === true || loading === true) {
      return (<BestOfferLoader />);
    }
    return (
      <div className={`payment-options-list ${brandName} ${mediaType}`}>
        <div className="row tab-row">
          <div className={`columns small-6 medium-6 pe-tab-wrapper  ${activeTab === tab1 ? 'active' : ''}`}>
            <h2 className="heading-second tab-heading" onClick={() => { this.changeTab(tab1) }}>
              <a className="heading-second pe-tab" >{heading1}</a>
            </h2>
          </div>
          <div className={`columns small-6 medium-6 pe-tab-wrapper end ${activeTab === tab2 ? 'active' : ''}`}>
            <h2 className="heading-second tab-heading" onClick={() => { this.changeTab(tab2) }}>
              <a className="heading-second pe-tab" >{heading2}</a>
            </h2>
          </div>
        </div>
        <div className="row">
          {option1}
          {option2}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div ref={(node) => { this.rootNode = node }} className="columns small-12 payment-options-list" onLoad={this.handleResize}>
        {this.renderPaymentOptions()}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  brandName: state.environment.BRAND_NAME,
  bestLease: getLeaseByTerm(state),
  bestFinance: getFinanceByTerm(state),
  loading: isPaymentOptionsLoading(state),
  mediaType: state.browser.mediaType
});

const mapDispatchToProps = dispatch => ({
  financeSelectDispatch: bindActionCreators(userSelectedFinanceTab, dispatch),
  leaseSelectDispatch: bindActionCreators(userSelectedLeaseTab, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(BestOfferList);



/** WEBPACK FOOTER **
 ** ./src/pages/TrimView/BestOfferList/index.js
 **/